// PortfolioDashboard.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../authContext';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faCheckCircle, faRotate, faCalculator, faMessage, faFileArrowDown, faRobot, faWallet} from '@fortawesome/free-solid-svg-icons';
import TradeStatsCalculator from './TradeCalculator';
import AlertPreferencesModal from './AlertPreferencesModal';
import ExportReportModal from './ExportReportModal';
import '../ai/AiTrades.css';
import './Dashboard.css';

export default function PortfolioDashboard() {
  const navigate = useNavigate();
  const { userId } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editEntry, setEditEntry] = useState({});
  const [modalItem, setModalItem] = useState(null);
  const [modalTarget, setModalTarget] = useState({ type: '', price: '' });
  const [telegramId, setTelegramId] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [showTelegramForm, setShowTelegramForm] = useState(false);
  const [editLatest, setEditLatest] = useState({});
  const [highlightEntries, setHighlightEntries] = useState({});
  const [highlightLatest, setHighlightLatest] = useState({});
  const [highlightTp, setHighlightTp] = useState({});
  const [highlightSl, setHighlightSl] = useState({});
  const [updatingTokenId, setUpdatingTokenId] = useState(null);
  const [showTelegramConfirm, setShowTelegramConfirm] = useState(false);
  const [telegramUsername, setTelegramUsername] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [editTp, setEditTp] = useState({});
  const [editSl, setEditSl] = useState({});
  const openItems = items.filter(i => !i.closed);
  const closedItems = items.filter(i => i.closed);
  const openStats = calculateStats(openItems);
  const closedStats = calculateStats(closedItems);
  const [showAlertPrefs, setShowAlertPrefs] = useState(false);
  const [alertHistory, setAlertHistory] = useState([]);
  const [showClosed, setShowClosed] = useState(false);
  const [showAlertHistory, setShowAlertHistory] = useState(false);
  const [showOngoingPositions, setShowOngoingPositions] = useState(true);
  const [showDeviceWarning, setShowDeviceWarning] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [sortTable, setSortTable] = useState(null);
  const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);




  const isLocal = window.location.hostname === 'localhost';
  const baseUrl = isLocal
    ? 'http://localhost:5000'
    : 'https://www.trendscout.club';

    const closeModal = () => setModalItem(null);

function calculateStats(items) {
  const total = items.length;
  let totalPL = 0;
  let countWithPL = 0;
  let wins = 0;
  let losses = 0;

  items.forEach(item => {
    const rec = item.recommendation?.toUpperCase() || "";
    const isLong = rec === "BUY" || rec === "STRONG BUY";
    const entry = isLong ? item.longPosition?.entry : item.shortPosition?.entry;
    const latest = item.latestPrice ?? entry;

    if (entry != null && latest != null) {
      const pl = isLong
        ? ((latest - entry) / entry) * 100
        : ((entry - latest) / entry) * 100;

      totalPL += pl;
      countWithPL++;

      if (pl >= 0) wins++;
      else losses++;
    }
  });

  const avgPL = countWithPL ? totalPL / countWithPL : 0;

  return {
    total,
    countWithPL,
    wins,
    losses,
    avgProfitLoss: avgPL,
    avgErrorMargin: 0, // extend if needed
    avgDaysToOutcome: 0, // extend if needed
    accuracyRatings: {
      Win: wins,
      Loss: losses,
    },
    tp: items.filter(i => i.outcome?.hit === 'tp').length,
    sl: items.filter(i => i.outcome?.hit === 'sl').length,
    neither: items.filter(i => i.outcome?.hit === 'neither').length
  };
}



    // Fetch user profile
  useEffect(() => {
    async function loadProfile() {
      try {
        const res = await fetch(`${baseUrl}/api/user`, { headers: { 'X-User-Id': userId } });
        const data = await res.json();
        setTelegramId(data.telegramChatId || null);
        setTelegramUsername(data.telegramUsername || null);
      } catch (err) {
        console.error('Failed to load profile', err);
      } finally {
        setProfileLoading(false);
      }
    }
    if (userId) loadProfile();
  }, [userId, baseUrl]);

  // Fetch portfolio items
  useEffect(() => {
    async function loadPortfolio() {
      try {
        const response = await fetch(`${baseUrl}/api/portfolio`, { headers: { 'X-User-Id': userId } });
        const data = await response.json();
        setItems(data);
      } catch (err) {
        console.error('Failed to load portfolio', err);
      } finally {
        setLoading(false);
      }
    }
    if (userId) loadPortfolio();
  }, [userId, baseUrl]);

  // Alert handler
  const handleAddAlert = async (itemId, { type, price }) => {
    if (!telegramId) return setShowTelegramForm(true);
    if (!type || !price) return alert('Choose type & price');
        const updatedAlerts = [{
          type,
          targetPrice: parseFloat(price),
          triggered: false
        }];
    try {
      const res = await fetch(`${baseUrl}/api/portfolio/${itemId}/alerts`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify({ alertTargets: updatedAlerts })
      });
      if (res.ok) setItems(items.map(i => i._id === itemId ? { ...i, alertTargets: updatedAlerts } : i));
    } catch (err) {
      console.error('Error updating alerts', err);
    }
  };

  // Fetch alert history
useEffect(() => {
  async function loadAlertHistory() {
    try {
      const res = await fetch(`${baseUrl}/api/user/${userId}/alert-history`);
      const data = await res.json();
      setAlertHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load alert history', err);
      setAlertHistory([]); // fallback to avoid null state
    }
  }
  if (userId) loadAlertHistory();
}, [userId, baseUrl]);



  // Remove item from portfolio
  const handleDeleteItem = async (itemId) => {
    try {
      const res = await fetch(`${baseUrl}/api/portfolio/${itemId}`, { method: 'DELETE', headers: { 'X-User-Id': userId } });
      if (res.ok) setItems(items.filter(i => i._id !== itemId));
    } catch (err) {
      console.error('Error deleting item', err);
    }
  };

  const sortItems = (items, key, direction) => {
  if (!key) return items;
  return [...items].sort((a, b) => {
    const getVal = (item) => {
      const rec = item.recommendation?.toUpperCase() || "";
      const isLong = rec === "BUY" || rec === "STRONG BUY";
      switch (key) {
        case 'token': return item.token.toUpperCase();
        case 'entry': return isLong ? item.longPosition?.entry ?? 0 : item.shortPosition?.entry ?? 0;
        case 'latest': return item.latestPrice ?? 0;
        case 'tp': return isLong ? item.longPosition?.tp ?? 0 : item.shortPosition?.tp ?? 0;
        case 'sl': return isLong ? item.longPosition?.sl ?? 0 : item.shortPosition?.sl ?? 0;
        case 'pl': {
          const entry = isLong ? item.longPosition?.entry : item.shortPosition?.entry;
          const latest = item.latestPrice ?? entry;
          if (!entry || !latest) return 0;
          return isLong ? (latest - entry) / entry : (entry - latest) / entry;
        }
        default: return 0;
      }
    };
    const valA = getVal(a);
    const valB = getVal(b);
    return typeof valA === 'string'
      ? (direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA))
      : (direction === 'asc' ? valA - valB : valB - valA);
  });
};

// ✅ Step 3: Apply sorting per section
const sortedOpenItems = sortItems(openItems, sortTable === 'open' ? sortKey : null, sortDirection);
const sortedClosedItems = sortItems(closedItems, sortTable === 'closed' ? sortKey : null, sortDirection);
const sortedAlertHistory = sortItems(alertHistory, sortTable === 'alert' ? sortKey : null, sortDirection);

// ✅ Step 4: Reusable handler
const handleSort = (key, table) => {
  if (sortKey === key && sortTable === table) {
    setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
  } else {
    setSortKey(key);
    setSortTable(table);
    setSortDirection('asc');
  }
};

const goToWallet = () => {
  navigate('/crg-wallet');
};

const goToFunds  = () => navigate('/me/funds');

  return (
    <div className='dashboard-main'>
      <div className="outcome-table-wrapper-dashboard">
        <div className="outcome-table">
          <h2>My Portfolio</h2>
              {profileLoading ? (
                <p>Loading profile…</p>
              ) : telegramId ? (
                <>
                <div className='dashboard-button-group'>  
                  <div className='dashboard-telegram-group'>
                      <p>
                        🔔 Alerts will be sent to Telegram:
                        <strong style={{ marginLeft: '6px' }}>
                          {telegramUsername
                            ? `@${telegramUsername}`
                            : telegramId}
                        </strong>
                        <div className='dashboard-telegram-button-box'>
                        <button onClick={() => setShowTelegramConfirm(true)} className="small-button" style={{ marginLeft: '10px' }}>
                          Change
                        </button>
                        </div>
                      </p>
                      </div>
                      
                       <div className='dashboard-telegram-group'>
                    <FontAwesomeIcon
                      icon={faFileArrowDown}
                      className="dashboard-menu-icons"
                      title="Download Report"
                      onClick={() => setShowExportModal(true)}
                    />
                    <FontAwesomeIcon
                      icon={faRobot}
                      title="Wallet Data"
                      className="dashboard-menu-icons"
                      onClick={goToWallet}
                    />
                    <FontAwesomeIcon
                      icon={faWallet}
                      title="My Funds"
                      className="dashboard-menu-icons"
                      onClick={goToFunds}         // 👈 hook it up
                      tabIndex={0}                // (optional a11y)
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') goToFunds(); }}
                    />
                    {userId === "pziPDtOgJiX06oGuJxNwxE" ? (
                      <FontAwesomeIcon
                        icon={faMessage}
                        className="dashboard-menu-icons"
                        title="Open Profit Calculator"
                        onClick={() => {
                          if (window.innerWidth < 1200) {
                            setShowDeviceWarning(true);
                          } else {
                            navigate('/ai-chat/00515166655gf4rr5df5v1f00055s');
                          }
                        }}
                        style={{ cursor: 'pointer', marginLeft: '12px' }}
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faMessage}
                        className="dashboard-menu-icons"
                        title="Access Restricted - Contact Admin"
                        onClick={() => setShowAccessDeniedModal(true)}
                        style={{ cursor: 'not-allowed', opacity: 0.5, marginLeft: '12px' }}
                      />
                    )}

                    <FontAwesomeIcon
                      icon={faCalculator}
                      className="dashboard-menu-icons"
                      title="Open Profit Calculator"
                      onClick={() => setShowCalculator(true)}
                      style={{ cursor: 'pointer', marginLeft: '12px' }}
                    />
                    <FontAwesomeIcon
                      icon={faBell}
                      title="Edit Alert Preferences"
                      className="dashboard-menu-icons"
                      onClick={() => setShowAlertPrefs(true)}
                    />
                    {showAlertPrefs && (
                      <AlertPreferencesModal userId={userId} onClose={() => setShowAlertPrefs(false)} />
                    )}
                    {showExportModal && (
                    <ExportReportModal items={items} onClose={() => setShowExportModal(false)} />
                  )}
                  </div>
                 </div>
                </>
              ) : (
              <>
                <a
                  href={`https://t.me/TrendScoutAlertsBot?start=${userId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="outcome-table button"
                  onClick={() => {
                    const interval = setInterval(async () => {
                      try {
                        const res = await fetch(`${baseUrl}/api/user`, {
                          headers: { 'X-User-Id': userId }
                        });
                        const data = await res.json();
                        if (data.telegramChatId) {
                          setTelegramId(data.telegramChatId);
                          clearInterval(interval);
                        }
                      } catch (err) {
                        console.error('Polling for Telegram ID failed', err);
                      }
                    }, 3000); // check every 3 seconds
                    setTimeout(() => clearInterval(interval), 30000); // stop after 30 sec max
                  }}
                >
                  Connect Telegram to enable alerts
                </a>
              </>

              )}


{(openStats || closedStats) && (
  <div className="expanded-content">
    {openStats && (
      <div className="expanded-section">
        <h4>📈 Ongoing</h4>
        <p><strong>Total Trades:</strong> {openStats.total}</p>
        {Object.entries(openStats.accuracyRatings).map(([label, count]) => (
          <p key={label}><strong>{label}:</strong> {count}</p>
        ))}
        <p><strong>P/L:</strong> {openStats.avgProfitLoss.toFixed(2)}%</p>
      </div>
    )}

    {closedStats && (
      <div className="expanded-section">
        <h4>📈 Closed</h4>
        <p><strong>Total Trades:</strong> {closedStats.total}</p>
        {Object.entries(closedStats.accuracyRatings).map(([label, count]) => (
          <p key={label}><strong>{label}:</strong> {count}</p>
        ))}
        <p><strong>P/L:</strong> {closedStats.avgProfitLoss.toFixed(2)}%</p>
      </div>
    )}
  </div>
)}


{showCalculator && (
  <div className="modal-overlay">
    <div className="calculator-modal">
      <TradeStatsCalculator items={items} />
      <div className="modal-buttons">
        <button onClick={() => setShowCalculator(false)}>Close</button>
      </div>
    </div>
  </div>
)}

<div className='dashboard-button-group-show'>
  <button
    className="small-button"
    onClick={() => setShowOngoingPositions(prev => !prev)}
  >
    {showOngoingPositions? "Hide Ongoing" : "Show Ongoing"}
  </button>

    <button
    className="small-button"
    onClick={() => setShowClosed(prev => !prev)}
  >
    {showClosed ? "Hide Closed" : "Show Closed"}
  </button>

    <button
    className="small-button"
    onClick={() => setShowAlertHistory(prev => !prev)}
  >
    {showAlertHistory ? "Hide Alerts" : "Show Alerts"}
  </button>
  </div>



       {showOngoingPositions && (
          <>
<h3 style={{ marginTop: '20px' }}>
  Ongoing Positions{" "}
</h3>
          <div className="table-scroll-container">
            {loading ? (
              <p style={{ padding: '16px', color: '#ccc' }}>Loading your portfolio…</p>
            ) : items.length === 0 ? (
              <p style={{ padding: '16px', color: '#ccc' }}>No items in your portfolio yet.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('token', 'open')}>Token {sortKey === 'token' && sortTable === 'open' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                    <th>initial</th>
                    <th>Type</th>
                    <th onClick={() => handleSort('entry', 'open')}>Entry {sortKey === 'entry' && sortTable === 'open' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                    <th onClick={() => handleSort('latest', 'open')}>Latest {sortKey === 'latest' && sortTable === 'open' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                    <th onClick={() => handleSort('tp', 'open')}>TP {sortKey === 'tp' && sortTable === 'open' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                    <th onClick={() => handleSort('sl', 'open')}>SL {sortKey === 'sl' && sortTable === 'open' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                    <th onClick={() => handleSort('pl', 'open')}>P/L (%) {sortKey === 'pl' && sortTable === 'open' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                    <th>Update</th>
                    <th>Add Alert</th>
                    <th>Close</th>
                    <th>Remove</th>
                  </tr>
                </thead>
                <tbody>
                    {sortedOpenItems.map(item => {
                      // 1) Pull the recommendation (BUY / STRONG BUY / SELL)
                      const rec = item.recommendation?.toUpperCase() || "";

                      // 2) Determine if this is a “long” or “short” position
                      const isLong = rec === "BUY" || rec === "STRONG BUY";

                      // 3) Extract the correct entry/TP/SL based on that
                      const entry = isLong
                        ? item.longPosition?.entry
                        : item.shortPosition?.entry;
                      const tp = isLong
                        ? item.longPosition?.tp
                        : item.shortPosition?.tp;
                      const sl = isLong
                        ? item.longPosition?.sl
                        : item.shortPosition?.sl;

                      const currentPrice = item.latestPrice != null
                        ? item.latestPrice
                        : entry;
                      // 4) Compute P/L
                        // new P/L calc, uses entry as fallback
                        let pl = "–";
                        if (entry != null) {
                          const diff = isLong
                            ? currentPrice - entry
                            : entry - currentPrice;
                          pl = ((diff / entry) * 100).toFixed(2);
                        }
                        const alert = item.alertTargets[0];

                      return (
                        <tr key={item._id}>
                          <td>
                              <a
                                href={`https://www.binance.com/en/trade/${item.token.replace("/", "_")}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="binance-chart-link"
                              >
                                {item.token}
                              </a>
                            </td>
                          <td>${item.entryPrice.toFixed(4)}</td>
                          <td>{rec}</td>
                        <td>
                          <input
                            type="number"
                            step="any"
                            value={editEntry[item._id] ?? entry ?? ''}
                            onChange={e => setEditEntry(prev => ({ ...prev, [item._id]: e.target.value }))}
                            onBlur={async () => {
                              const newPrice = editEntry[item._id];
                              if (!newPrice) return;
                              // tell the server which side we’re editing:
                              const position = isLong ? 'long' : 'short';
                              try {
                                const res = await fetch(`${baseUrl}/api/portfolio/${item._id}`, {
                                  method: 'PATCH',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'X-User-Id': userId
                                  },
                                  body: JSON.stringify({
                                    entryPrice: parseFloat(newPrice),
                                    position
                                  })
                                });
                              if (res.ok) {
                                const updated = await res.json();
                                setItems(items.map(i => i._id === item._id ? updated : i));
                                // animate
                                setHighlightEntries(prev => ({ ...prev, [item._id]: true }));
                                setTimeout(() => {
                                  setHighlightEntries(prev => {
                                    const c = { ...prev };
                                    delete c[item._id];
                                    return c;
                                  });
                                }, 1000);
                              } else {
                                console.error('Failed to update latestPrice', await res.text());
                              }
                              } catch (err) {
                                console.error('Error updating entryPrice', err);
                              }
                            }}
                            disabled={!telegramId}
                            className={`dashboard-input ${highlightEntries[item._id] ? 'input-highlight' : ''}`}
                          />
                        </td>

                          <td>
                        <input
                          type="number"
                          step="any"
                          value={editLatest[item._id] ?? (item.latestPrice ?? entry) ?? ''}
                          onChange={e =>
                            setEditLatest(prev => ({ ...prev, [item._id]: e.target.value }))
                          }
                          onBlur={async () => {
                            const newPrice = editLatest[item._id];
                            if (!newPrice) return;
                            try {
                              const res = await fetch(
                                `${baseUrl}/api/portfolio/${item._id}/latest`,
                                {
                                  method: 'PATCH',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'X-User-Id': userId
                                  },
                                  body: JSON.stringify({ latestPrice: newPrice })
                                }
                              );
                              if (res.ok) {
                                const updated = await res.json();
                                setItems(items.map(i => i._id === item._id ? updated : i));
                                // animate
                                setHighlightLatest(prev => ({ ...prev, [item._id]: true }));
                                setTimeout(() => {
                                  setHighlightLatest(prev => {
                                    const c = { ...prev };
                                    delete c[item._id];
                                    return c;
                                  });
                                }, 1000);
                              } else {
                                console.error('Failed to update latestPrice', await res.text());
                              }
                            } catch (err) {
                              console.error('Error updating latestPrice', err);
                            }
                          }}
                          disabled={!telegramId}
                          className={`dashboard-input ${highlightLatest[item._id] ? 'input-highlight' : ''}`}
                        />
                      </td>
                        <td>
                          <input
                            type="number"
                            step="any"
                            value={editTp[item._id] ?? (tp ?? '')}
                            onChange={(e) => setEditTp(prev => ({ ...prev, [item._id]: e.target.value }))}
                            onBlur={async () => {
                              const newTp = parseFloat(editTp[item._id]);
                              if (!newTp) return;
                              try {
                                const res = await fetch(`${baseUrl}/api/portfolio/${item._id}`, {
                                  method: 'PATCH',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'X-User-Id': userId,
                                  },
                                  body: JSON.stringify({
                                    tp: newTp,
                                    position: isLong ? 'long' : 'short',
                                  }),
                                });
                                if (res.ok) {
                                  const updated = await res.json();
                                  setItems(items.map(i => i._id === item._id ? updated : i));
                                  // 🔄 Animate TP
                                  setHighlightTp(prev => ({ ...prev, [item._id]: true }));
                                  setTimeout(() => {
                                    setHighlightTp(prev => {
                                      const c = { ...prev };
                                      delete c[item._id];
                                      return c;
                                    });
                                  }, 1000);
                                } else {
                                  console.error('Failed to update TP:', await res.text());
                                }
                              } catch (err) {
                                console.error('Error updating TP:', err);
                              }
                            }}
                            disabled={!telegramId}
                            className={`dashboard-input ${highlightTp[item._id] ? 'input-highlight' : ''}`}
                          />
                        </td>


                        <td>
                          <input
                            type="number"
                            step="any"
                            value={editSl[item._id] ?? (sl ?? '')}
                            onChange={(e) => setEditSl(prev => ({ ...prev, [item._id]: e.target.value }))}
                            onBlur={async () => {
                              const newSl = parseFloat(editSl[item._id]);
                              if (!newSl) return;
                              try {
                                const res = await fetch(`${baseUrl}/api/portfolio/${item._id}`, {
                                  method: 'PATCH',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'X-User-Id': userId,
                                  },
                                  body: JSON.stringify({
                                    sl: newSl,
                                    position: isLong ? 'long' : 'short',
                                  }),
                                });
                                if (res.ok) {
                                  const updated = await res.json();
                                  setItems(items.map(i => i._id === item._id ? updated : i));
                                  // 🔄 Animate SL
                                  setHighlightSl(prev => ({ ...prev, [item._id]: true }));
                                  setTimeout(() => {
                                    setHighlightSl(prev => {
                                      const c = { ...prev };
                                      delete c[item._id];
                                      return c;
                                    });
                                  }, 1000);
                                } else {
                                  console.error('Failed to update SL:', await res.text());
                                }
                              } catch (err) {
                                console.error('Error updating SL:', err);
                              }
                            }}
                            disabled={!telegramId}
                            className={`dashboard-input ${highlightSl[item._id] ? 'input-highlight' : ''}`}
                          />
                        </td>



                          <td style={{ color: pl >= 0 ? "limegreen" : "red" }}>
                            {pl === "–" ? "–" : `${pl}%`}
                          </td>
                          <td>
                      <FontAwesomeIcon
                        icon={faRotate}
                        spin={updatingTokenId === item._id}
                        className="rotate-icon"
                        title="Fetch Latest Price"
                        style={{
                          cursor: 'pointer',
                          color: updatingTokenId === item._id ? '#888' : '#1f77ff',
                        }}
                        onClick={async () => {
                          setUpdatingTokenId(item._id);
                          try {
                            const res = await fetch(`${baseUrl}/api/portfolio/${item._id}/refresh-price`, {
                              method: 'POST',
                              headers: { 'X-User-Id': userId }
                            });
                            const updated = await res.json();
                            setItems(items.map(i => i._id === item._id ? updated : i));
                            setItems(items.map(i => i._id === item._id ? updated : i));

                            // 🔄 Trigger highlight animation
                            setHighlightLatest(prev => ({ ...prev, [item._id]: true }));
                            setTimeout(() => {
                              setHighlightLatest(prev => {
                                const copy = { ...prev };
                                delete copy[item._id];
                                return copy;
                              });
                            }, 1000);

                          } catch (err) {
                            console.error('Error refreshing price:', err);
                          } finally {
                            setUpdatingTokenId(null);
                          }
                        }}
                      />
                    </td>

                          <td>
                              {alert ? (
                                <button
                                  className={`alert-label ${alert.triggered ? 'alert-triggered' : ''}`}
                                  onClick={() => {
                                    setModalItem(item);
                                    setModalTarget({ type: alert.type, price: alert.targetPrice });
                                  }}
                                  title={alert.triggered ? "Alert triggered" : "Alert active"}
                                >
                                  <>
                                    {alert.type.toUpperCase()}: ${alert.targetPrice}
                                    {alert.triggered && (
                                      <FontAwesomeIcon className='alert-triggered-icon'
                                        icon={faCheckCircle}                                       
                                        title="Alert triggered"
                                      />
                                    )}
                                  </>
                                </button>
                              ) : (
                                <FontAwesomeIcon
                                  icon={faBell}
                                  className="bell-icon"
                                  title="Set Alert"
                                  onClick={() => {
                                    setModalItem(item);
                                    setModalTarget({ type: '', price: '' });
                                  }}
                                />
                              )}

                          </td>
                          <td>
                          {!item.closed && (
                            <button
                              className="small-button"
                              onClick={async () => {
                                try {
                                  const res = await fetch(`${baseUrl}/api/portfolio/${item._id}/close`, {
                                    method: 'PATCH',
                                    headers: { 'X-User-Id': userId }
                                  });
                                  const updated = await res.json();
                                  setItems(prev => prev.map(i => i._id === item._id ? updated : i));
                                } catch (err) {
                                  console.error('Error closing position', err);
                                }
                              }}
                            >
                              Close
                            </button>
                          )}
                        </td>

                        <td>
                          <button onClick={() => handleDeleteItem(item._id)}>
                            ❌
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          </>
            )}


          {showClosed && (
              <>
<h3 style={{ marginTop: '20px' }}>
  Closed Positions{" "}
</h3>
          <div className="table-scroll-container">
            {loading ? (
              <p style={{ padding: '16px', color: '#ccc' }}>Loading your portfolio…</p>
            ) : items.length === 0 ? (
              <p style={{ padding: '16px', color: '#ccc' }}>No items in your portfolio yet.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('token', 'closed')}>Token {sortKey === 'token' && sortTable === 'closed' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                    <th>Type</th>
                    <th onClick={() => handleSort('entry', 'closed')}>Entry {sortKey === 'entry' && sortTable === 'closed' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                    <th onClick={() => handleSort('latest', 'closed')}>Latest {sortKey === 'latest' && sortTable === 'closed' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                    <th onClick={() => handleSort('tp', 'closed')}>TP {sortKey === 'tp' && sortTable === 'closed' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                    <th onClick={() => handleSort('sl', 'closed')}>SL {sortKey === 'sl' && sortTable === 'closed' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                    <th onClick={() => handleSort('pl', 'closed')}>P/L (%) {sortKey === 'pl' && sortTable === 'closed' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                    <th>Remove</th>
                  </tr>
                </thead>
                <tbody>
                    {sortedClosedItems.map(item => {
                      // 1) Pull the recommendation (BUY / STRONG BUY / SELL)
                      const rec = item.recommendation?.toUpperCase() || "";

                      // 2) Determine if this is a “long” or “short” position
                      const isLong = rec === "BUY" || rec === "STRONG BUY";

                      // 3) Extract the correct entry/TP/SL based on that
                      const entry = isLong
                        ? item.longPosition?.entry
                        : item.shortPosition?.entry;
                      const tp = isLong
                        ? item.longPosition?.tp
                        : item.shortPosition?.tp;
                      const sl = isLong
                        ? item.longPosition?.sl
                        : item.shortPosition?.sl;

                      const currentPrice = item.latestPrice != null
                        ? item.latestPrice
                        : entry;
                      // 4) Compute P/L
                        // new P/L calc, uses entry as fallback
                        let pl = "–";
                        if (entry != null) {
                          const diff = isLong
                            ? currentPrice - entry
                            : entry - currentPrice;
                          pl = ((diff / entry) * 100).toFixed(2);
                        }

                      return (
                        <tr key={item._id}>
                          <td>
                              <a
                                href={`https://www.binance.com/en/trade/${item.token.replace("/", "_")}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="binance-chart-link"
                              >
                                {item.token}
                              </a>
                            </td>
                          <td>{rec}</td>
                          <td>${item.entryPrice.toFixed(4)}</td>
                          <td>${item.latestPrice.toFixed(4)}</td>
                          <td>${tp.toFixed(4)}</td>
                          <td>${sl.toFixed(4)}</td>
                          <td style={{ color: pl >= 0 ? "limegreen" : "red" }}>
                            {pl === "–" ? "–" : `${pl}%`}
                          </td>
                        <td>
                          <button onClick={() => handleDeleteItem(item._id)}>
                            ❌
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          </>
             )}


{showAlertHistory && (
  <>   
<h3 style={{ marginTop: '20px' }}>
  Alert History{" "}
</h3>
<div className="table-scroll-container">
  {loading ? (
    <p style={{ padding: '16px', color: '#ccc' }}>Loading alert history…</p>
  ) : alertHistory.length === 0 ? (
    <p style={{ padding: '16px', color: '#ccc' }}>No alerts triggered yet.</p>
  ) : (
    <table>
      <thead>
        <tr>
          <th onClick={() => handleSort('token', 'open')}>Token {sortKey === 'token' && sortTable === 'open' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
          <th>Initial</th>
          <th>Type</th>
<th onClick={() => handleSort('entry', 'alert')}>Entry {sortKey === 'entry' && sortTable === 'alert' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
<th onClick={() => handleSort('latest', 'alert')}>Latest {sortKey === 'latest' && sortTable === 'alert' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
<th onClick={() => handleSort('tp', 'alert')}>TP {sortKey === 'tp' && sortTable === 'alert' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
<th onClick={() => handleSort('sl', 'alert')}>SL {sortKey === 'sl' && sortTable === 'alert' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
<th onClick={() => handleSort('pl', 'alert')}>P/L (%) {sortKey === 'pl' && sortTable === 'alert' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
        </tr>
      </thead>
      <tbody>
        {sortedAlertHistory.map((item, idx) => {
          const rec = item.recommendation?.toUpperCase() || '';
          const isLong = rec === 'BUY' || rec === 'STRONG BUY';
          const entry = isLong ? item.longPosition?.entry : item.shortPosition?.entry;
          const tp = isLong ? item.longPosition?.tp : item.shortPosition?.tp;
          const sl = isLong ? item.longPosition?.sl : item.shortPosition?.sl;
          const currentPrice = item.latestPrice != null ? item.latestPrice : entry;
          let pl = '–';
          if (entry != null) {
            const diff = isLong
              ? currentPrice - entry
              : entry - currentPrice;
            pl = ((diff / entry) * 100).toFixed(2);
          }

          return (
            <tr key={idx}>
              <td>
                <a
                  href={`https://www.binance.com/en/trade/${item.token.replace("/", "_")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="binance-chart-link"
                >
                  {item.token}
                </a>
              </td>
              <td>${item.entryPrice?.toFixed(4) || '–'}</td>
              <td>{rec}</td>
              <td>${entry?.toFixed(4) || '–'}</td>
              <td>${currentPrice?.toFixed(4) || '–'}</td>
              <td>${tp?.toFixed(4) || '–'}</td>
              <td>${sl?.toFixed(4) || '–'}</td>
              <td style={{ color: pl >= 0 ? 'limegreen' : 'red' }}>
                {pl === '–' ? '–' : `${pl}%`}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  )}
</div>
  </>
  )}


        </div>
      </div>
      {modalItem && (
  <div className="modal-overlay">
    <div className="modal">
      <h3>Set Alert for {modalItem.token}</h3>
      <label>
        Type:
        <select
          value={modalTarget.type}
          onChange={e => setModalTarget(prev => ({ ...prev, type: e.target.value }))}
        >
          <option value="">Select…</option>
          <option value="tp">TP</option>
          <option value="sl">SL</option>
          <option value="price">Price</option>
        </select>
      </label>
      <label>
        Price:
        <input
          type="number"
          value={modalTarget.price}
          onChange={e => setModalTarget(prev => ({ ...prev, price: e.target.value }))}
        />
      </label>
      <div className="modal-buttons">
          <button
            onClick={async () => {
              await handleAddAlert(modalItem._id, modalTarget);
              setModalItem(null);
            }}
          >
            Save Alert
          </button>
          + {modalItem.alertTargets.length > 0 && (
   <button
     onClick={async () => {
       // clear all alerts
       try {
         const res = await fetch(
           `${baseUrl}/api/portfolio/${modalItem._id}/alerts`,
           {
             method: 'PATCH',
             headers: {
               'Content-Type': 'application/json',
               'X-User-Id': userId
             },
             body: JSON.stringify({ alertTargets: [] })
           }
         );
         if (res.ok) {
           setItems(items.map(i =>
             i._id === modalItem._id
               ? { ...i, alertTargets: [] }
               : i
           ));
         }
       } catch (err) {
         console.error('Error clearing alerts', err);
       } finally {
         setModalItem(null);
       }
     }}
   >
     Clear Alerts
   </button>
 )}
        <button onClick={closeModal}>Cancel</button>
      </div>
    </div>
  </div>
)}

{showTelegramConfirm && (
  <div className="modal-overlay">
    <div className="modal">
      <h3>Change Telegram ID?</h3>
      <p>This will remove your current Telegram ID and require reconnecting via Telegram Bot.</p>
      <div className="modal-buttons">
        <button
          onClick={async () => {
            try {
              await fetch(`${baseUrl}/api/user/telegram-id`, {
                method: 'DELETE',
                headers: { 'X-User-Id': userId }
              });
              setTelegramId(null);         // Update UI
              setShowTelegramConfirm(false);
              setShowTelegramForm(false);  // Optional cleanup
            } catch (err) {
              console.error('Failed to delete Telegram ID', err);
              alert('Failed to unlink Telegram ID');
            }
          }}
        >
          Yes, Remove
        </button>
        <button onClick={() => setShowTelegramConfirm(false)}>
          Cancel
        </button>
      </div>
    </div>
  </div>
)}
{showDeviceWarning && (
  <div className="device-popup-overlay">
    <div className="device-popup">
      <h3>Not Available on Mobile</h3>
      <p>This feature is only available on laptop or desktop devices.</p>
      <button onClick={() => setShowDeviceWarning(false)}>OK</button>
    </div>
  </div>
)}

{showAccessDeniedModal && (
  <div className="info-modal">
    <div className="info-modal-content">
      <h3>Restricted Access</h3>
      <p>This feature is currently only available to approved users.</p>
      <p>Please contact an admin if you'd like to request access.</p>
      <button
        onClick={() => setShowAccessDeniedModal(false)}
        style={{
          marginTop: "1rem",
          padding: "8px 18px",
          background: "#006eff",
          borderRadius: "6px",
          border: "none",
          color: "#fff",
          fontSize: "14px",
          cursor: "pointer"
        }}
      >
        Close
      </button>
    </div>
  </div>
)}

    </div>
  );
}
